import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { of } from 'rxjs';
import { finalize, map, switchMap, take } from 'rxjs/operators';

import { ConfirmActionComponent } from '@website-builder/dialogs/confirm-action/confirm-action.component';
import { DialogService } from '@core/dialog/shared/services/dialog.service';
import { ConfirmActionInput } from '@website-builder/dialogs/confirm-action/model/confirm-action-input';
import { ConfirmActionInputType } from '@website-builder/dialogs/confirm-action/model/confirm-action-input-type.enum';
import { BuilderTemplateService } from '@website-builder/application/builder-template.service';

export const templateUnsavedGuard: CanDeactivateFn<unknown> = () => {
  const templateService = inject(BuilderTemplateService);
  const dialog = inject(DialogService);

  if (!templateService.isTemplateDirty()) {
    return true;
  }

  const data: ConfirmActionInput = {
    message: 'You have unsaved changes. Save before exiting?',
    confirmButtonMsg: 'Save',
    dismissButtonMsg: 'Cancel',
    discardButtonMsg: 'Discard',
    confirmButtonClass: 'uk-button-primary',
  };

  const ref = dialog.open<ConfirmActionComponent, ConfirmActionInputType>(ConfirmActionComponent, {
    title: 'Unsaved changes',
    style: 'website-guard',
    overPanel: true,
    data,
  });

  return ref.afterClosed$().pipe(
    take(1),
    switchMap(result => {
      switch (result) {
        case ConfirmActionInputType.CONFIRM:
          return templateService.saveTemplate().pipe(
            take(1),
            map(() => true),
            finalize(() => true),
          );
        case ConfirmActionInputType.DISCARD:
          templateService.resetDraftTemplateState();
          return of(true);
        case ConfirmActionInputType.CANCEL:
          return of(false);
      }
    }),
  );
};
